import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext'; // Import LanguageProvider
import Layout from './Layout';
import Home from './pages/Home';
import Browse from './pages/Browse';
import ItemDetail from './pages/ItemDetail';
import CreateItem from './pages/CreateItem';
import MyItems from './pages/MyItems';
import Messages from './pages/Messages';
import Profile from './pages/Profile';
import PublicProfile from './pages/PublicProfile';
import Admin from './pages/Admin';
import CoinPanel from './pages/CoinPanel'; // Import CoinPanel
import MessageScreen from './pages/MessageScreen';
import Login from './pages/Login';
import Register from './pages/Register'; // Import Register component
import LandingPage from './pages/Landing';
import WelcomePage from './pages/WelcomePage';
import PrivateRoute from './components/common/PrivateRoute';
import StaticPage from './pages/StaticPages';
import ScrollToTopReset from './components/common/ScrollToTopReset';
import InitialLoader from './components/common/InitialLoader';
import VerifyEmail from './pages/VerifyEmail';
import { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';

const queryClient = new QueryClient();

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isLoading) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.touchAction = 'unset';
    }

    const timer = setTimeout(() => {
      setIsLoading(false);
      document.body.style.overflow = 'unset';
      document.body.style.touchAction = 'unset';
    }, 2000);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = 'unset';
      document.body.style.touchAction = 'unset';
    };
  }, [isLoading]);

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <LanguageProvider> {/* Wrap with LanguageProvider */}
          <AnimatePresence>
            {isLoading && <InitialLoader />}
          </AnimatePresence>
          <ThemeProvider>
            <CurrencyProvider>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <ScrollToTopReset />
                <div className="min-h-screen">
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/welcome" element={<WelcomePage />} />
                      <Route path="/browse" element={<Browse listingType="item" />} />
                      <Route path="/item/:id" element={<ItemDetail />} />
                      
                      {/* Protected Routes */}
                      <Route path="/create" element={<CreateItem />} />
                      
                      <Route element={<PrivateRoute />}>
                        <Route path="/edit-item/:id" element={<CreateItem />} />
                        <Route path="/my-items" element={<MyItems />} />
                        <Route path="/messages" element={<Messages />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/profile/:id" element={<PublicProfile />} />
                        <Route path="/admin" element={<Admin />} />
                        <Route path="/coins" element={<CoinPanel />} />
                        <Route path="/messages/:id" element={<MessageScreen />} />
                      </Route>

                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} /> {/* Add Register route */}
                      <Route path="/verify-email" element={<VerifyEmail />} />
                      
                      {/* Static Pages */}
                      <Route path="/about" element={<LandingPage />} />
                      <Route path="/faq" element={<StaticPage />} />
                      <Route path="/careers" element={<StaticPage />} />
                      <Route path="/press" element={<StaticPage />} />
                      <Route path="/terms" element={<StaticPage />} />
                      <Route path="/privacy" element={<StaticPage />} />
                      <Route path="/company" element={<StaticPage />} />
                    </Routes>
                  </Layout>
                </div>
              </BrowserRouter>
            </CurrencyProvider>
          </ThemeProvider>
        </LanguageProvider> {/* Close LanguageProvider */}
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
